import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import apiService from "../services/api";
import { generateWebhookUrl } from "../utils/webhookUtils";
import { useAuth } from "../contexts/AuthContext";
import { LoadingSpinner } from "../components/ui/loading-spinner";

// Hook personalizado para debounce
const useDebounce = (callback, delay) => {
  const timeoutRef = useRef(null);

  const debouncedCallback = useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);

  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return [debouncedCallback, cancel];
};

const ConnectWhatsApp = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Estados principais
  const [instanceExists, setInstanceExists] = useState(false);
  const [instanceStatus, setInstanceStatus] = useState(null);
  const [qrCode, setQrCode] = useState(null);

  // Estados de controle
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: 'success'
  });

  // Estados para controle de erro e autenticação
  const [error, setError] = useState(null);

  // Loading states específicos e consistentes
  const [loadingStates, setLoadingStates] = useState({
    initial: true,
    status: false,
    qrCode: false,
    creating: false,
    deleting: false,
    auth: false
  });

  const setLoadingState = useCallback((key, value) => {
    setLoadingStates(prev => ({ ...prev, [key]: value }));
  }, []);

  // Sistema de cache para evitar requisições desnecessárias
  const [cache, setCache] = useState({
    lastStatusCheck: null,
    lastQRCodeFetch: null,
    statusData: null,
    qrCodeData: null
  });

  const CACHE_DURATION = {
    STATUS: 5000,
    QRCODE: 10000
  };

  const isOnConnectWhatsAppPage = location.pathname === '/connect-whatsapp';

  const instanceStatusRef = useRef(instanceStatus);
  const checkInstanceStatusRef = useRef();
  const qrCodeRef = useRef(qrCode);

  const fastPollingIntervalRef = useRef(null);
  const slowPollingIntervalRef = useRef(null);

  useEffect(() => {
    instanceStatusRef.current = instanceStatus;
  }, [instanceStatus]);

  useEffect(() => {
    qrCodeRef.current = qrCode;
  }, [qrCode]);

  useEffect(() => {
    if (error) {
      const timeout = setTimeout(() => {
        setError(null);
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, [error]);

  const instanceName = user?.target_db_name || 'principal';
  const WEBHOOK_URL = generateWebhookUrl(user?.target_db_name);

  const showNotification = useCallback((message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
    }, 5000);
  }, []);

  const showNotificationAdvanced = useCallback((message, type = 'info', duration = 5000) => {
    return Date.now();
  }, []);

  const showError = useCallback((message, duration = 8000) => {
    setError(message);
    showNotificationAdvanced(message, 'error', duration);
  }, [showNotificationAdvanced]);

  const showSuccess = useCallback((message, duration = 5000) => {
    setError('');
    showNotification(message, 'success');
    showNotificationAdvanced(message, 'success', duration);
  }, [showNotification, showNotificationAdvanced]);

  const handleAuthError = useCallback((error) => {
    if (error.message?.includes('TokenExpiredError') ||
        error.message?.includes('401') ||
        error.message?.includes('Unauthorized')) {
      showError('Sua sessão expirou. Redirecionando para login...');
      localStorage.removeItem('token');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
      return true;
    }
    return false;
  }, [navigate, showError]);

  const handleApiError = useCallback((error, context = '') => {
    if (handleAuthError(error)) {
      return;
    }

    let errorMessage = 'Erro desconhecido';

    if (error.response) {
      const status = error.response.status;
      const data = error.response.data;

      switch (status) {
        case 400:
          errorMessage = data?.message || 'Dados inválidos enviados';
          break;
        case 404:
          errorMessage = 'Recurso não encontrado. Verifique se a Evolution API está configurada';
          break;
        case 500:
          errorMessage = 'Erro interno do servidor. Tente novamente em alguns minutos';
          break;
        case 503:
          errorMessage = 'Serviço temporariamente indisponível';
          break;
        default:
          errorMessage = data?.message || `Erro HTTP ${status}`;
      }
    } else if (error.request) {
      errorMessage = 'Erro de conexão. Verifique sua internet e se a API está funcionando';
    } else {
      errorMessage = error.message || 'Erro inesperado';
    }

    showError(`${context ? context + ': ' : ''}${errorMessage}`);
  }, [handleAuthError, showError]);

  const getQRCode = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh && cache.lastQRCodeFetch) {
      const timeSinceLastFetch = Date.now() - cache.lastQRCodeFetch;
      if (timeSinceLastFetch < CACHE_DURATION.QRCODE && cache.qrCodeData) {
        setQrCode(cache.qrCodeData);
        return true;
      }
    }

    setLoadingState('qrCode', true);
    try {
      const response = await apiService.request(`/evolution/instance/qrcode/${instanceName}`);

      if (response.success && response.qrcode) {
        const newQrCode = {
          code: response.qrcode.code,
          base64: response.qrcode.base64
        };

        if (!qrCode || qrCode.code !== newQrCode.code) {
          setQrCode(newQrCode);
          setCache(prev => ({
            ...prev,
            lastQRCodeFetch: Date.now(),
            qrCodeData: newQrCode
          }));
        }

        return true;
      } else {
        if (response.message && response.message.includes('já estar conectada')) {
          setQrCode(null);
          setCache(prev => ({
            ...prev,
            qrCodeData: null,
            lastQRCodeFetch: null
          }));
          return false;
        }

        handleApiError(new Error(response.message || 'Resposta inválida da API'), 'Obter QR Code');
        return false;
      }
    } catch (error) {
      handleApiError(error, 'Obter QR Code');
      return false;
    } finally {
      setLoadingState('qrCode', false);
    }
  }, [qrCode, handleApiError, cache.lastQRCodeFetch, cache.qrCodeData, CACHE_DURATION.QRCODE, setLoadingState, instanceName]);

  const checkInstanceStatus = useCallback(async (skipQRCode = false, showLoadingIndicator = false, forceRefresh = false) => {
    if (!forceRefresh && cache.lastStatusCheck) {
      const timeSinceLastCheck = Date.now() - cache.lastStatusCheck;
      if (timeSinceLastCheck < CACHE_DURATION.STATUS && cache.statusData) {
        const cachedData = cache.statusData;
        setInstanceExists(cachedData.exists);
        setInstanceStatus(cachedData.status);
        return;
      }
    }

    if (showLoadingIndicator) {
      setLoadingState('status', true);
    }

    try {
      const response = await apiService.request(`/evolution/instance/status/${instanceName}`);

      if (response.success && response.data) {
        const instanceData = response.data;
        const currentStatus = instanceData.connectionStatus || 'unknown';
        const previousStatus = instanceStatusRef.current?.state;
        const statusChanged = previousStatus !== currentStatus;

        if (statusChanged) {
          setInstanceExists(true);
          setInstanceStatus({
            instanceName: instanceData.name || instanceName,
            state: currentStatus,
            status: currentStatus
          });

          if (currentStatus === 'open') {
            showSuccess('WhatsApp conectado! A IA está funcionando!');
            setQrCode(null);
          } else if (!skipQRCode && (currentStatus === 'close' || currentStatus === 'connecting')) {
            await getQRCode(true);
          }
        } else {
          if (loadingStates.initial) {
            setInstanceExists(true);
            setInstanceStatus({
              instanceName: instanceData.name || instanceName,
              state: currentStatus,
              status: currentStatus
            });

            if (!skipQRCode && currentStatus !== 'open' && !qrCode) {
              await getQRCode(true);
            }
          } else if (!skipQRCode && currentStatus === 'connecting' && !qrCode) {
            await getQRCode(true);
          }
        }

        setCache(prev => ({
          ...prev,
          lastStatusCheck: Date.now(),
          statusData: {
            exists: true,
            status: {
              instanceName: instanceData.name || instanceName,
              state: currentStatus,
              status: currentStatus
            }
          }
        }));
      } else {
        if (instanceStatusRef.current !== null) {
          setInstanceExists(false);
          setInstanceStatus(null);
          setQrCode(null);
        }

        setCache(prev => ({
          ...prev,
          lastStatusCheck: Date.now(),
          statusData: {
            exists: false,
            status: null
          }
        }));

        if (!skipQRCode && !qrCode && loadingStates.initial) {
          const qrSuccess = await getQRCode(true);

          if (qrSuccess) {
            setInstanceExists(true);
            setInstanceStatus({
              instanceName: instanceName,
              state: 'connecting',
              status: 'connecting'
            });
          }
        }
      }
    } catch (error) {
      if (error.response?.data?.availableInstances) {
        showError(
          `Instância "${instanceName}" não encontrada. Instâncias disponíveis: ${error.response.data.availableInstances.join(', ')}`
        );
      } else {
        handleApiError(error, 'Verificar status da instância');
      }

      if (instanceStatusRef.current !== null) {
        setInstanceExists(false);
        setInstanceStatus(null);
        setQrCode(null);
      }
    } finally {
      if (showLoadingIndicator) {
        setLoadingState('status', false);
      }

      if (loadingStates.initial) {
        setLoadingState('initial', false);
      }
    }
  }, [getQRCode, loadingStates.initial, showSuccess, showError, handleApiError, qrCode, cache.lastStatusCheck, cache.statusData, CACHE_DURATION.STATUS, setLoadingState, instanceName]);

  const [debouncedGetQRCode, cancelGetQRCode] = useDebounce(getQRCode, 1000);
  const [debouncedCheckStatus, cancelCheckStatus] = useDebounce(checkInstanceStatus, 500);

  useEffect(() => {
    checkInstanceStatusRef.current = checkInstanceStatus;
  }, [checkInstanceStatus]);

  useEffect(() => {
    const checkAuth = async () => {
      setLoadingState('auth', true);
      try {
        const response = await apiService.request('/auth/me');
        if (!response.success) {
          handleAuthError({ response: { status: 401 } });
          return;
        }
      } catch (error) {
        handleAuthError(error);
      } finally {
        setLoadingState('auth', false);
      }
    };
    checkAuth();
  }, [handleAuthError, setLoadingState]);

  useEffect(() => {
    if (isOnConnectWhatsAppPage) {
      checkInstanceStatusRef.current?.();
    }
  }, [isOnConnectWhatsAppPage]);

  useEffect(() => {
    if (fastPollingIntervalRef.current) {
      clearInterval(fastPollingIntervalRef.current);
      fastPollingIntervalRef.current = null;
    }
    if (slowPollingIntervalRef.current) {
      clearInterval(slowPollingIntervalRef.current);
      slowPollingIntervalRef.current = null;
    }

    if (!isOnConnectWhatsAppPage) {
      return;
    }

    const currentState = instanceStatus?.state;
    let pollingDelay;
    let shouldSkipQRCode;

    if (currentState === 'open') {
      pollingDelay = 30000;
      shouldSkipQRCode = true;
    } else if (currentState === 'connecting' || currentState === 'close' || !instanceExists) {
      pollingDelay = 5000;
      shouldSkipQRCode = false;
    } else {
      pollingDelay = 10000;
      shouldSkipQRCode = true;
    }

    const pollingInterval = setInterval(() => {
      if (location.pathname !== '/connect-whatsapp') {
        clearInterval(pollingInterval);
        return;
      }
      checkInstanceStatusRef.current?.(shouldSkipQRCode, false);
    }, pollingDelay);

    if (pollingDelay <= 5000) {
      fastPollingIntervalRef.current = pollingInterval;
    } else {
      slowPollingIntervalRef.current = pollingInterval;
    }

    return () => {
      clearInterval(pollingInterval);
      if (fastPollingIntervalRef.current === pollingInterval) {
        fastPollingIntervalRef.current = null;
      }
      if (slowPollingIntervalRef.current === pollingInterval) {
        slowPollingIntervalRef.current = null;
      }
    };
  }, [isOnConnectWhatsAppPage, instanceStatus?.state, instanceExists, location.pathname]);

  useEffect(() => {
    return () => {
      if (fastPollingIntervalRef.current) {
        clearInterval(fastPollingIntervalRef.current);
        fastPollingIntervalRef.current = null;
      }
      if (slowPollingIntervalRef.current) {
        clearInterval(slowPollingIntervalRef.current);
        slowPollingIntervalRef.current = null;
      }
      cancelGetQRCode();
      cancelCheckStatus();
    };
  }, [cancelGetQRCode, cancelCheckStatus]);

  const createInstance = useCallback(async () => {
    setLoadingState('creating', true);
    setError('');

    try {
      const instanceData = {
        instanceName: instanceName,
        qrcode: true,
        integration: 'WHATSAPP-BAILEYS',
        webhook: {
          url: WEBHOOK_URL,
          byEvents: false,
          base64: true,
          events: [
            'QRCODE_UPDATED',
            'MESSAGES_UPSERT',
            'CONNECTION_UPDATE'
          ]
        },
        rejectCall: false,
        alwaysOnline: true,
        readStatus: true
      };

      const response = await apiService.request('/evolution/instance/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(instanceData)
      });

      if (response.success) {
        if (response.qrcode && response.qrcode.base64 && response.qrcode.code) {
          const immediateQrCode = {
            code: response.qrcode.code,
            base64: response.qrcode.base64
          };
          setQrCode(immediateQrCode);
          setInstanceExists(true);
          setInstanceStatus({
            instanceName: instanceName,
            state: 'connecting',
            status: 'connecting'
          });
          showSuccess('Instância criada! QR Code disponível para escaneamento.');
        }

        await checkInstanceStatus();

        if (!qrCodeRef.current) {
          await new Promise(resolve => setTimeout(resolve, 2000));

          let attempts = 0;
          const maxAttempts = 15;

          const waitForQRCode = async () => {
            try {
              await checkInstanceStatus(false, false);

              if (!qrCodeRef.current && attempts < maxAttempts - 1) {
                attempts++;
                setTimeout(waitForQRCode, 2000);
              } else if (!qrCodeRef.current) {
                showError('QR Code não disponível. Tente gerar manualmente.');
              } else {
                showSuccess('QR Code disponível! Escaneie para conectar.');
              }
            } catch (error) {
              if (attempts < maxAttempts - 1) {
                attempts++;
                setTimeout(waitForQRCode, 2000);
              }
            }
          };

          waitForQRCode();
        }
      } else {
        showError('Erro ao criar instância: ' + (response.message || 'Erro desconhecido'));
      }
    } catch (error) {
      if (error.response?.status === 409) {
        showError('Instância já existe. Verificando status...');
        await checkInstanceStatus(false, false);
      } else {
        handleApiError(error, 'Criar instância do WhatsApp');
      }
    } finally {
      setLoadingState('creating', false);
    }
  }, [checkInstanceStatus, showSuccess, showError, handleApiError, qrCodeRef, setLoadingState, WEBHOOK_URL, instanceName]);

  const deleteInstance = useCallback(async () => {
    setLoadingState('deleting', true);
    try {
      const response = await apiService.request(`/evolution/instance/delete/${instanceName}`, {
        method: 'DELETE'
      });

      if (response.success) {
        setInstanceExists(false);
        setInstanceStatus(null);
        setQrCode(null);
        showSuccess('Instância deletada com sucesso!');
      } else {
        showError('Erro ao deletar instância');
      }
    } catch (error) {
      handleApiError(error, 'Deletar instância');
    } finally {
      setLoadingState('deleting', false);
      setDeleteDialogOpen(false);
    }
  }, [showSuccess, showError, handleApiError, setLoadingState, instanceName]);

  const isConnected = instanceStatus?.state === 'open';
  const isConnecting = instanceStatus?.state === 'connecting';

  if (loadingStates.initial) {
    return (
      <div className="w-full min-h-[calc(100vh-64px)] flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto text-on-surface font-body">
      <div className="p-4 sm:p-8 space-y-6 sm:space-y-8">
        {/* Page header */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div className="space-y-1">
            <h2 className="text-2xl sm:text-4xl font-extrabold font-headline tracking-tight text-on-surface">Conectar WhatsApp</h2>
            <p className="text-on-surface-variant font-medium font-body">Configure e gerencie sua conexão com o WhatsApp Business</p>
          </div>
        </div>

        {/* Notification banner */}
        {notification.show && (
          <div className={`flex items-center gap-3 px-5 py-4 rounded-xl border text-sm font-medium ${
            notification.type === 'success'
              ? 'bg-primary/10 border-primary/30 text-primary'
              : notification.type === 'error'
              ? 'bg-error/10 border-error/30 text-error'
              : 'bg-surface-container border-outline-variant/10 text-on-surface-variant'
          }`}>
            <span className="material-symbols-outlined text-[20px]">
              {notification.type === 'success' ? 'check_circle' : notification.type === 'error' ? 'error' : 'info'}
            </span>
            <span>{notification.message}</span>
          </div>
        )}

        {/* Error banner */}
        {error && (
          <div className="flex items-center gap-3 px-5 py-4 rounded-xl border bg-error/10 border-error/30 text-error text-sm font-medium">
            <span className="material-symbols-outlined text-[20px]">error</span>
            <span>{error}</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Status Card — col-span-7 */}
          <div className="lg:col-span-7 bg-surface-container rounded-xl border border-white/5 overflow-hidden relative">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-50 pointer-events-none" />
            <div className="relative p-8 flex flex-col h-full">
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-xl font-headline font-bold text-on-surface">Status da Instância</h3>
                {loadingStates.status && (
                  <span className="material-symbols-outlined text-primary text-[20px] animate-spin">refresh</span>
                )}
              </div>

              <div className="flex-1 flex flex-col justify-center space-y-6">
                {instanceExists && instanceStatus ? (
                  <>
                    {/* Connected state */}
                    {isConnected && (
                      <div className="p-8 rounded-2xl bg-surface-container-high/30 border border-primary/20 flex flex-col items-center text-center">
                        <div className="relative mb-6">
                          <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center animate-pulse">
                            <span
                              className="material-symbols-outlined text-primary"
                              style={{ fontSize: '56px', fontVariationSettings: "'FILL' 1" }}
                            >
                              sensors
                            </span>
                          </div>
                          <div className="absolute bottom-1 right-1 w-6 h-6 bg-primary rounded-full border-4 border-surface flex items-center justify-center">
                            <span className="material-symbols-outlined text-on-primary" style={{ fontSize: '14px' }}>check</span>
                          </div>
                        </div>
                        <h4 className="text-2xl font-headline font-bold text-on-surface mb-2">WhatsApp Conectado</h4>
                        <p className="text-on-surface-variant text-sm max-w-xs">
                          A IA está ativa na instância{' '}
                          <span className="text-primary font-mono font-bold">{instanceStatus.instanceName}</span>
                          {' '}e processando mensagens em tempo real.
                        </p>
                      </div>
                    )}

                    {/* Connecting / QR Code state */}
                    {!isConnected && qrCode && (
                      <div className="p-6 rounded-2xl bg-surface-container-high/30 border border-outline-variant/10">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-2">
                            <span className="material-symbols-outlined text-primary text-[20px]">qr_code_2</span>
                            <h4 className="font-headline font-bold text-on-surface">QR Code do WhatsApp</h4>
                          </div>
                          <button
                            onClick={() => debouncedGetQRCode(true)}
                            disabled={loadingStates.qrCode}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-outline-variant/10 text-on-surface-variant hover:text-on-surface hover:bg-surface-container-high transition-colors text-sm disabled:opacity-50"
                          >
                            <span className={`material-symbols-outlined text-[18px] ${loadingStates.qrCode ? 'animate-spin' : ''}`}>refresh</span>
                            <span>Atualizar QR</span>
                          </button>
                        </div>
                        <div className="flex flex-col items-center gap-4">
                          <div className="bg-white p-4 rounded-xl inline-block shadow-lg">
                            <img
                              src={qrCode.base64}
                              alt="QR Code WhatsApp"
                              className="w-56 h-56"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          </div>
                          <p className="text-on-surface-variant text-sm text-center">
                            Abra o WhatsApp no seu celular e escaneie este código
                          </p>
                          <p className="text-on-surface-variant/60 text-xs text-center">
                            Se o QR Code expirar, clique em "Atualizar QR" para gerar um novo
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Waiting for QR code */}
                    {!isConnected && !qrCode && (
                      <div className="p-8 rounded-2xl bg-surface-container-high/30 border border-outline-variant/10 flex flex-col items-center text-center gap-4">
                        <span className="material-symbols-outlined text-on-surface-variant" style={{ fontSize: '40px' }}>qr_code_2</span>
                        <p className="text-on-surface-variant text-sm">
                          {isConnecting ? 'Aguardando QR Code...' : 'Instância desconectada'}
                        </p>
                        <button
                          onClick={() => debouncedGetQRCode(true)}
                          disabled={loadingStates.qrCode}
                          className="bg-primary text-on-primary hover:bg-primary/90 rounded-lg px-4 py-2 font-semibold flex items-center gap-2 disabled:opacity-50 transition-colors"
                        >
                          <span className={`material-symbols-outlined text-[18px] ${loadingStates.qrCode ? 'animate-spin' : ''}`}>
                            {loadingStates.qrCode ? 'refresh' : 'qr_code_scanner'}
                          </span>
                          <span>Gerar QR Code</span>
                        </button>
                      </div>
                    )}

                    {/* Status badge */}
                    <div className="flex items-center gap-3">
                      <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold ${
                        isConnected
                          ? 'bg-primary/10 text-primary'
                          : 'bg-zinc-800 text-zinc-400'
                      }`}>
                        <span className={`w-2 h-2 rounded-full ${isConnected ? 'bg-primary' : 'bg-zinc-500'}`} />
                        {isConnected ? 'Ativa' : isConnecting ? 'Conectando' : 'Desconectada'}
                      </span>
                      <span className="text-xs text-on-surface-variant bg-surface-container-high px-3 py-1.5 rounded-full">
                        Instância: {instanceStatus.instanceName}
                      </span>
                    </div>
                  </>
                ) : (
                  /* No instance */
                  <div className="p-8 rounded-2xl bg-surface-container-high/30 border border-outline-variant/10 flex flex-col items-center text-center gap-3">
                    <span className="material-symbols-outlined text-on-surface-variant" style={{ fontSize: '40px' }}>
                      settings_input_component
                    </span>
                    <h4 className="text-lg font-headline font-bold text-on-surface">Nenhuma instância encontrada</h4>
                    <p className="text-on-surface-variant text-sm max-w-xs">
                      Crie uma nova instância para conectar o WhatsApp.
                    </p>
                  </div>
                )}
              </div>

              {/* Refresh status button */}
              <div className="mt-8">
                <button
                  onClick={() => debouncedCheckStatus(false, true)}
                  disabled={loadingStates.status}
                  className="w-full py-3.5 rounded-lg bg-transparent border-2 border-primary text-primary font-bold font-headline hover:bg-primary hover:text-on-primary transition-all active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <span className={`material-symbols-outlined text-[20px] ${loadingStates.status ? 'animate-spin' : ''}`}>refresh</span>
                  Atualizar Status
                </button>
              </div>
            </div>
          </div>

          {/* Management Card — col-span-5 */}
          <div className="lg:col-span-5 bg-surface-container rounded-xl border border-white/5 flex flex-col">
            <div className="p-8 flex-1 flex flex-col">
              <div className="mb-8">
                <h3 className="text-xl font-headline font-bold text-on-surface mb-2">
                  Crie ou Exclua a Instância
                </h3>
                <p className="text-on-surface-variant text-sm">
                  {instanceExists
                    ? 'Gerencie o ciclo de vida da sua integração atual.'
                    : 'Crie uma nova instância para conectar o WhatsApp.'}
                </p>
              </div>

              {instanceExists ? (
                <>
                  <div className="p-6 rounded-xl bg-error/5 border border-error/20 mb-8 flex gap-4">
                    <div className="shrink-0 w-12 h-12 bg-error/10 rounded-lg flex items-center justify-center">
                      <span
                        className="material-symbols-outlined text-error"
                        style={{ fontVariationSettings: "'FILL' 1" }}
                      >
                        delete_sweep
                      </span>
                    </div>
                    <div>
                      <p className="text-on-surface font-bold mb-1">Instância Ativa</p>
                      <p className="text-on-surface-variant text-sm leading-relaxed">
                        Excluir a instância interromperá a comunicação da IA imediatamente. Esta ação não pode ser desfeita.
                      </p>
                    </div>
                  </div>
                  <div className="flex-1" />
                  <button
                    onClick={() => setDeleteDialogOpen(true)}
                    disabled={loadingStates.deleting}
                    className="w-full py-4 rounded-lg bg-red-700 hover:bg-red-600 text-white font-black font-headline shadow-lg transition-all active:scale-[0.98] flex items-center justify-center gap-3 disabled:opacity-50"
                  >
                    {loadingStates.deleting ? (
                      <>
                        <span className="material-symbols-outlined text-[20px] animate-spin">refresh</span>
                        <span>Excluindo...</span>
                      </>
                    ) : (
                      <>
                        <span className="material-symbols-outlined text-[20px]">delete_forever</span>
                        <span>Excluir Instância WhatsApp</span>
                      </>
                    )}
                  </button>
                </>
              ) : (
                <>
                  <div className="p-6 rounded-xl bg-primary/5 border border-primary/20 mb-8 flex gap-4">
                    <div className="shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <span
                        className="material-symbols-outlined text-primary"
                        style={{ fontVariationSettings: "'FILL' 1" }}
                      >
                        add_circle
                      </span>
                    </div>
                    <div>
                      <p className="text-on-surface font-bold mb-1">Pronto para conectar</p>
                      <p className="text-on-surface-variant text-sm leading-relaxed">
                        Clique no botão abaixo para criar sua instância do WhatsApp e obter o QR Code de conexão.
                      </p>
                    </div>
                  </div>
                  <div className="flex-1" />
                  <button
                    onClick={createInstance}
                    disabled={loadingStates.creating}
                    className="w-full py-4 rounded-lg bg-primary text-on-primary font-black font-headline shadow-lg hover:bg-primary/90 transition-all active:scale-[0.98] flex items-center justify-center gap-3 disabled:opacity-50"
                  >
                    {loadingStates.creating ? (
                      <>
                        <span className="material-symbols-outlined text-[20px] animate-spin">refresh</span>
                        <span>Criando Instância...</span>
                      </>
                    ) : (
                      <>
                        <span className="material-symbols-outlined text-[20px]">add_circle</span>
                        <span>Criar Instância WhatsApp</span>
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Support banner */}
        <div className="p-8 bg-surface-container rounded-xl border-l-4 border-primary border border-white/5 flex items-center justify-between gap-6">
          <div className="flex items-center gap-6">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
              <span className="material-symbols-outlined text-primary">contact_support</span>
            </div>
            <div>
              <h4 className="text-on-surface font-bold font-headline">Precisa de ajuda com a integração?</h4>
              <p className="text-on-surface-variant text-sm">Fale com nosso time de suporte para configurar seu WhatsApp Business API.</p>
            </div>
          </div>
          <a
            href="https://wa.me/5541987718296"
            className="shrink-0 px-6 py-3 bg-primary text-on-primary text-sm font-bold rounded-full hover:brightness-110 transition-all shadow-lg flex items-center gap-2"
          >
            <span className="material-symbols-outlined text-[18px]">chat</span>
            Entre em Contato
          </a>
        </div>
      </div>

      {/* Delete confirmation dialog */}
      {deleteDialogOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-surface-container rounded-xl border border-white/5 shadow-2xl max-w-md w-full mx-4 p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-error/10 rounded-lg flex items-center justify-center shrink-0">
                <span className="material-symbols-outlined text-error">delete_forever</span>
              </div>
              <h3 className="text-lg font-headline font-bold text-on-surface">Confirmar Exclusão</h3>
            </div>
            <p className="text-on-surface-variant text-sm mb-8 leading-relaxed">
              Tem certeza que deseja excluir a instância do WhatsApp? Esta ação não pode ser desfeita e você precisará reconectar o WhatsApp.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteDialogOpen(false)}
                className="px-4 py-2 rounded-lg border border-outline-variant/10 text-on-surface-variant hover:bg-surface-container-high transition-colors text-sm font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={deleteInstance}
                disabled={loadingStates.deleting}
                className="flex items-center gap-2 px-4 py-2 bg-red-700 hover:bg-red-600 text-white rounded-lg font-semibold text-sm transition-all disabled:opacity-50"
              >
                {loadingStates.deleting ? (
                  <>
                    <span className="material-symbols-outlined text-[16px] animate-spin">refresh</span>
                    <span>Excluindo...</span>
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined text-[16px]">delete_forever</span>
                    <span>Excluir</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectWhatsApp;
